"use client";

/**
 * fund-yearly-chart.tsx
 *
 * Vertical bar chart showing calendar-year returns for the last 5-7 years.
 * Positive years are green, negative red. A dashed horizontal line at 0 sits
 * between positive and negative zones.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Performance Analysis section)
 */

export interface FundYearlyChartProps {
  yearlyReturns: Array<{ year: number; returnPct: number }> | null;
}

const CHART_HEIGHT = 220;

export function FundYearlyChart({ yearlyReturns }: FundYearlyChartProps) {
  if (!yearlyReturns || yearlyReturns.length === 0) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Yearly return data not available.
        </p>
      </div>
    );
  }

  const sorted = [...yearlyReturns].sort((a, b) => a.year - b.year);

  const maxPos = Math.max(0, ...sorted.map((y) => y.returnPct));
  const minNeg = Math.min(0, ...sorted.map((y) => y.returnPct));
  const absMax = Math.max(Math.abs(maxPos), Math.abs(minNeg), 1);

  // Allocate chart space above/below the zero line proportionally
  const posRatio = maxPos / absMax;
  const negRatio = Math.abs(minNeg) / absMax;
  const totalRatio = posRatio + negRatio || 1;
  const posHeight = (posRatio / totalRatio) * CHART_HEIGHT;
  const negHeight = (negRatio / totalRatio) * CHART_HEIGHT;

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="text-foreground text-sm font-serif mb-1">
        Yearly returns
      </div>
      <div className="text-dim text-[11px] mb-4">
        Calendar-year performance
      </div>

      <div className="flex items-end justify-around gap-3 relative" style={{ height: CHART_HEIGHT }}>
        {/* Zero line */}
        <div
          className="absolute left-0 right-0 border-t border-dashed border-surface-light/20"
          style={{ top: posHeight }}
        />

        {sorted.map((item) => {
          const isPositive = item.returnPct >= 0;
          const barHeight =
            (Math.abs(item.returnPct) / absMax) *
            (isPositive ? posHeight : negHeight);

          return (
            <div
              key={item.year}
              className="flex-1 flex flex-col items-center relative"
              style={{ height: CHART_HEIGHT }}
            >
              {/* Positive zone */}
              <div
                className="w-full flex flex-col items-center justify-end"
                style={{ height: posHeight }}
              >
                {isPositive && (
                  <>
                    <div className="text-cgreen text-[10px] font-bold font-tabular mb-1">
                      +{item.returnPct.toFixed(1)}%
                    </div>
                    <div
                      className="w-full max-w-[48px] rounded-t transition-all duration-700"
                      style={{
                        height: `${barHeight}px`,
                        backgroundColor: "#10B981",
                      }}
                    />
                  </>
                )}
              </div>

              {/* Negative zone */}
              <div
                className="w-full flex flex-col items-center justify-start"
                style={{ height: negHeight }}
              >
                {!isPositive && (
                  <>
                    <div
                      className="w-full max-w-[48px] rounded-b transition-all duration-700"
                      style={{
                        height: `${barHeight}px`,
                        backgroundColor: "#EF4444",
                      }}
                    />
                    <div className="text-cred text-[10px] font-bold font-tabular mt-1">
                      {item.returnPct.toFixed(1)}%
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Year labels */}
      <div className="flex items-center justify-around gap-3 mt-2">
        {sorted.map((item) => (
          <div
            key={item.year}
            className="flex-1 text-center text-muted-foreground text-[11px] font-tabular"
          >
            {item.year}
          </div>
        ))}
      </div>
    </div>
  );
}
